import { useEffect, useSyncExternalStore } from 'react';
import { de } from '@fsv/core';
import { version } from '../version';
import { checksForUpdates, fetchServedStamp, isUpdate, reloadPage, VERSION_POLL_MS, type Stamp } from '../lib/versionCheck';
import { Icon } from './Icon';
import './UpdateBand.css';

// One flag for the whole page: the poll sets it, and so does a part that
// failed to load (lazyPart below) — both mean "this tab is older than the server".
let available = false;
const listeners = new Set<() => void>();

export function markUpdateAvailable(): void {
  if (available) return;
  available = true;
  for (const l of listeners) l();
}

function subscribe(cb: () => void) {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

/** For tests only. */
export function resetUpdateFlag(): void {
  available = false;
}

/**
 * Asks for /version.json every five minutes and whenever the tab becomes
 * visible again, until it finds another build. A `dev` bundle never asks.
 */
export function useUpdateAvailable(running: Stamp = version, fetcher: typeof fetch = fetch): boolean {
  const isAvailable = useSyncExternalStore(subscribe, () => available, () => false);
  useEffect(() => {
    if (!checksForUpdates(running) || isAvailable) return;
    let alive = true;
    const check = async () => {
      const served = await fetchServedStamp(fetcher);
      if (alive && isUpdate(running, served)) markUpdateAvailable();
    };
    const onVisible = () => {
      if (document.visibilityState === 'visible') void check();
    };
    const timer = window.setInterval(() => void check(), VERSION_POLL_MS);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      alive = false;
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [running, fetcher, isAvailable]);
  return isAvailable;
}

/** The band above the top bar. Reloading is the person's decision: open input would be lost. */
export function UpdateBand({ running, fetcher }: { running?: Stamp; fetcher?: typeof fetch }) {
  const show = useUpdateAvailable(running, fetcher);
  if (!show) return null;
  return (
    <div className="update-band" role="status" data-testid="update-band">
      <p className="update-band__text">
        <Icon name="refresh" size={16} />
        {de.update.available}
      </p>
      <button type="button" className="update-band__btn" onClick={() => void reloadPage()}>
        {de.update.reload}
      </button>
    </div>
  );
}

/**
 * In place of a part that could not be loaded. After a deployment the old
 * tab's chunk names no longer exist on the server; this says so instead of
 * a blank page. (Offline the chunks come from the service worker's precache.)
 */
export function PartMissing() {
  useEffect(() => markUpdateAvailable(), []);
  return (
    <div className="page page--narrow">
      <p className="update-missing" role="alert">
        {de.update.partMissing}{' '}
        <button type="button" className="update-band__btn" onClick={() => void reloadPage()}>
          {de.update.reload}
        </button>
      </p>
    </div>
  );
}
